/**
 * GRIDHEIMR — Persona Card Component
 *
 * Displays individual persona information from hatched agents.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import type { AgentIdentityPublic } from "../api/types";
import { BLUE, YELLOW, DARK_BG, PIXEL, MONO } from "../theme";

/**
 * Component props
 */
interface PersonaCardProps {
  agent: AgentIdentityPublic;
  onHatch?: (agentId: string) => void;
  onView?: (agentId: string) => void;
}

/**
 * Get MBTI type display properties
 */
function getMBTIDisplay(mbtiType?: string): {
  icon: string;
  color: string;
  label: string;
} {
  if (!mbtiType) {
    return { icon: "🧬", color: "rgba(255,255,255,0.3)", label: "Unknown" };
  }

  // MBTI personality type colors — GRIDHEIMR palette
  const mbtiColors: Record<string, string> = {
    INTJ: "#8b5cf6",
    INTP: "#6366f1",
    ENTJ: "#ec4899",
    ENTP: YELLOW,
    INFJ: "#00c97a",
    INFP: "#14b8a6",
    ENFJ: "#84cc16",
    ENFP: "#eab308",
    ISTJ: BLUE,
    ISFJ: "#06b6d4",
    ESTJ: "#ef4444",
    ESFJ: "#f97316",
    ISTP: "#64748b",
    ISFP: "#78716c",
    ESTP: "#dc2626",
    ESFP: "#ea580c",
  };

  const color = mbtiColors[mbtiType.toUpperCase()] || "rgba(255,255,255,0.3)";
  return { icon: "🧬", color, label: mbtiType.toUpperCase() };
}

/**
 * Get gender expression icon
 */
function getGenderIcon(genderExpression: string): string {
  const expr = genderExpression.toLowerCase();
  if (expr.includes("feminine")) return "♀️";
  if (expr.includes("masculine")) return "♂️";
  if (expr.includes("androgynous") || expr.includes("non-binary")) return "⚧️";
  return "👤";
}

/**
 * Format timestamp to relative time
 */
function formatRelativeTime(timestamp?: string): string {
  if (!timestamp) return "Never";

  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

/**
 * Truncate text to a maximum length
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

/**
 * Persona Card Component
 *
 * Displays persona information including name, appearance, psychology,
 * and MBTI type from a hatched agent.
 */
export function PersonaCard({ agent, onHatch, onView }: PersonaCardProps) {
  const navigate = useNavigate();
  const persona = agent.persona;

  // If no persona, show placeholder
  if (!persona) {
    return (
      <div style={styles.card}>
        <div style={styles.placeholderText}>No persona data available</div>
      </div>
    );
  }

  const mbtiDisplay = getMBTIDisplay(persona.mbti_type);
  const genderIcon = getGenderIcon(persona.gender_expression);

  /**
   * Navigate to agent detail page
   */
  const handleCardClick = () => {
    navigate(`/hatchery/agents/${agent.agent_id}`);
  };

  return (
    <div
      style={styles.card}
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = BLUE;
        e.currentTarget.style.boxShadow = `0 0 20px rgba(0,106,167,0.3)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(0,106,167,0.2)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.genderIcon}>{genderIcon}</span>
          <div>
            <h3 style={styles.personaName}>{persona.name}</h3>
            <p style={styles.genderExpression}>{persona.gender_expression}</p>
          </div>
        </div>
        <div style={{ ...styles.mbtiBadge, borderColor: mbtiDisplay.color, color: mbtiDisplay.color }}>
          <span style={styles.mbtiIcon}>{mbtiDisplay.icon}</span>
          <span style={styles.mbtiLabel}>{mbtiDisplay.label}</span>
        </div>
      </div>

      {/* Appearance & Voice */}
      <div style={styles.section}>
        <div style={styles.subsection}>
          <span style={styles.subsectionLabel}>Appearance</span>
          <p style={styles.subsectionText}>{truncateText(persona.appearance, 100)}</p>
        </div>
        <div style={styles.subsection}>
          <span style={styles.subsectionLabel}>Voice</span>
          <p style={styles.subsectionText}>{truncateText(persona.voice, 80)}</p>
        </div>
      </div>

      {/* Psychology */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>Psychology</span>
        <p style={styles.sectionText}>{truncateText(persona.psychology, 120)}</p>
      </div>

      {/* Strengths */}
      {persona.strengths && persona.strengths.length > 0 && (
        <div style={styles.strengthsSection}>
          <span style={styles.sectionLabel}>Strengths</span>
          <div style={styles.strengthsList}>
            {persona.strengths.slice(0, 3).map((strength, index) => (
              <span key={index} style={styles.strengthBadge}>
                {strength}
              </span>
            ))}
            {persona.strengths.length > 3 && (
              <span style={styles.strengthBadge}>+{persona.strengths.length - 3} more</span>
            )}
          </div>
        </div>
      )}

      {/* Info Grid */}
      <div style={styles.infoGrid}>
        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>Agent ID</span>
          <span style={styles.infoValue}>{agent.agent_id.substring(0, 8)}...</span>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>Platform</span>
          <span style={styles.infoValue}>{agent.platform}</span>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>Block</span>
          <span style={styles.infoValue}>#{agent.block_index}</span>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>Hatched</span>
          <span style={styles.infoValue}>{formatRelativeTime(agent.hatched_at)}</span>
        </div>
      </div>

      {/* Actions */}
      <div style={styles.actions}>
        {onView && (
          <button
            style={{ ...styles.actionButton, ...styles.viewButton }}
            onClick={(e) => {
              e.stopPropagation();
              onView(agent.agent_id);
            }}
          >
            👁️ View Details
          </button>
        )}
        {onHatch && (
          <button
            style={{ ...styles.actionButton, ...styles.hatchButton }}
            onClick={(e) => {
              e.stopPropagation();
              onHatch(agent.agent_id);
            }}
          >
            🥚 Hatch Similar
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Component styles
 */
const styles: Record<string, React.CSSProperties> = {
  card: {
    background: DARK_BG,
    padding: "24px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: `1px solid rgba(0,106,167,0.2)`,
    position: "relative",
    overflow: "hidden",
  },

  placeholderText: {
    fontFamily: MONO,
    color: "rgba(255,255,255,0.3)",
    textAlign: "center",
    padding: "40px 20px",
    fontSize: "0.85rem",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "16px",
  },

  headerLeft: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flex: 1,
  },

  genderIcon: {
    fontSize: "32px",
  },

  personaName: {
    margin: 0,
    fontFamily: MONO,
    fontSize: "1rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: "4px",
    letterSpacing: "0.04em",
  },

  genderExpression: {
    margin: 0,
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.35)",
    textTransform: "capitalize",
  },

  mbtiBadge: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    border: "1px solid",
    fontFamily: PIXEL,
    fontSize: "0.42rem",
    fontWeight: 400,
    flexShrink: 0,
    letterSpacing: "0.08em",
  },

  mbtiIcon: {
    fontSize: "14px",
  },

  mbtiLabel: {
    letterSpacing: "0.08em",
  },

  section: {
    marginBottom: "12px",
  },

  subsection: {
    marginBottom: "8px",
  },

  sectionLabel: {
    display: "block",
    fontFamily: PIXEL,
    fontSize: "0.35rem",
    fontWeight: 400,
    color: YELLOW,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    marginBottom: "8px",
    textShadow: `0 0 6px rgba(254,204,0,0.25)`,
  },

  subsectionLabel: {
    display: "block",
    fontFamily: PIXEL,
    fontSize: "0.3rem",
    fontWeight: 400,
    color: "rgba(255,255,255,0.35)",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: "6px",
  },

  sectionText: {
    margin: 0,
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.7)",
    lineHeight: "1.6",
  },

  subsectionText: {
    margin: 0,
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.6)",
    lineHeight: "1.5",
  },

  strengthsSection: {
    marginBottom: "12px",
  },

  strengthsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },

  strengthBadge: {
    background: "rgba(0,106,167,0.08)",
    color: "rgba(255,255,255,0.7)",
    padding: "5px 10px",
    fontFamily: MONO,
    fontSize: "0.75rem",
    border: `1px solid rgba(0,106,167,0.15)`,
    transition: "all 0.2s ease",
  },

  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "12px",
    marginBottom: "18px",
    paddingTop: "16px",
    borderTop: `1px solid rgba(0,106,167,0.12)`,
  },

  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  infoLabel: {
    fontFamily: PIXEL,
    fontSize: "0.3rem",
    color: "rgba(255,255,255,0.35)",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },

  infoValue: {
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.75)",
    fontWeight: 600,
  },

  actions: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    borderTop: `1px solid rgba(0,106,167,0.12)`,
    paddingTop: "16px",
  },

  actionButton: {
    flex: 1,
    minWidth: "130px",
    padding: "10px 16px",
    border: "1px solid",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    letterSpacing: "0.06em",
    background: "transparent",
  },

  viewButton: {
    borderColor: BLUE,
    color: BLUE,
  },

  hatchButton: {
    borderColor: YELLOW,
    color: YELLOW,
  },
};
